
import React from 'react';
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import type { Application } from '@/types/database.types';

export interface CarrierInfoProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
}

export const CarrierInfo = ({
  register,
  watch,
  setValue
}: CarrierInfoProps) => {
  const wasCancelled = watch('wc_coverage_cancelled');

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Workers' Compensation Carrier Information</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Please provide information about your current workers' compensation insurance.
      </p>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="current_workers_comp_carrier">Current Workers' Comp Carrier</Label>
            <Input
              id="current_workers_comp_carrier"
              {...register('current_workers_comp_carrier')}
              placeholder="Enter carrier name"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="workers_comp_renewal_date">Renewal Date</Label>
            <Input
              id="workers_comp_renewal_date"
              type="date"
              {...register('workers_comp_renewal_date')}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="wc_coverage_cancelled" 
              checked={wasCancelled}
              onCheckedChange={(checked) => {
                setValue('wc_coverage_cancelled', checked === true);
                if (!checked) {
                  setValue('wc_cancellation_reason', '');
                }
              }}
            />
            <Label htmlFor="wc_coverage_cancelled">
              Has your workers' compensation coverage ever been cancelled or non-renewed?
            </Label>
          </div>
          
          {wasCancelled && (
            <div className="pl-6 pt-2">
              <Label htmlFor="wc_cancellation_reason">Please explain:</Label>
              <Textarea
                id="wc_cancellation_reason"
                {...register('wc_cancellation_reason')}
                placeholder="Provide details about the cancellation or non-renewal"
              />
            </div>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="company_services_description">Provide a detailed description of your company's services:</Label>
          <Textarea
            id="company_services_description"
            {...register('company_services_description')}
            placeholder="Describe what your company does"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="physical_operations_description">Describe the physical operations of your business:</Label>
          <Textarea
            id="physical_operations_description"
            {...register('physical_operations_description')}
            placeholder="Describe the physical aspects of your operations"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="has_physical_stock" 
              checked={watch('has_physical_stock')}
              onCheckedChange={(checked) => {
                setValue('has_physical_stock', checked === true);
              }}
            />
            <Label htmlFor="has_physical_stock">
              Do you maintain physical inventory/stock?
            </Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="provides_engineering_services" 
              checked={watch('provides_engineering_services')}
              onCheckedChange={(checked) => {
                setValue('provides_engineering_services', checked === true);
              }}
            />
            <Label htmlFor="provides_engineering_services">
              Do you provide engineering services?
            </Label>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="has_usl_jones_exposure" 
            checked={watch('has_usl_jones_exposure')}
            onCheckedChange={(checked) => {
              setValue('has_usl_jones_exposure', checked === true);
            }}
          />
          <Label htmlFor="has_usl_jones_exposure">
            Do you have any USL&H or Jones Act exposure?
          </Label>
        </div>
      </div>
    </div>
  );
};
